import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from './api';
import { useAuth } from './AuthContext';
import './Objects.css';

const Objects = () => {
  const { hasRole, user } = useAuth();
  const [objects, setObjects] = useState([]);
  const [branches, setBranches] = useState([]);
  const [branchFilter, setBranchFilter] = useState('');
  const [viewMode, setViewMode] = useState('table'); // 'table' | 'grouped'
  const [collapsedBranches, setCollapsedBranches] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingObject, setEditingObject] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    branch_id: '',
    address: '',
    description: '',
    shift_start: '06:00',
    shift_end: '21:00',
    grace_period_mins: 15,
    is_active: true,
  });
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const perPage = viewMode === 'grouped' ? 500 : 20;

  const fetchBranches = async () => {
    if (!hasRole(['admin'])) return;
    try {
      const response = await api.get('/branches', { params: { per_page: 100 } });
      setBranches(response.data.items);
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  const fetchObjects = useCallback(async () => {
    setLoading(true);
    try {
      const params = { per_page: perPage, page, active_only: false };
      if (search) params.search = search;
      if (branchFilter) params.branch_id = branchFilter;

      const response = await api.get('/objects', { params });
      setObjects(response.data.items);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Error fetching objects:', error);
    } finally {
      setLoading(false);
    }
  }, [page, search, branchFilter, perPage]);

  useEffect(() => {
    fetchBranches();
  }, []);

  useEffect(() => {
    fetchObjects();
  }, [fetchObjects]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchObjects();
  };

  const handleBranchFilterChange = (e) => {
    setBranchFilter(e.target.value);
    setPage(1);
  };

  const toggleBranchCollapse = (branchKey) => {
    setCollapsedBranches((prev) => ({
      ...prev,
      [branchKey]: !prev[branchKey],
    }));
  };

  // Group objects by branch
  const groupedObjects = useMemo(() => {
    const groups = {};
    for (const obj of objects) {
      const key = obj.branch_name || 'Без филиала';
      if (!groups[key]) {
        groups[key] = {
          name: key,
          branchId: obj.branch_id,
          items: [],
          total: 0,
          active: 0,
        };
      }
      groups[key].items.push(obj);
      groups[key].total++;
      if (obj.is_active) groups[key].active++;
    }
    return Object.values(groups);
  }, [objects]);

  const fetchNextCode = async (branchId = '') => {
    try {
      const params = branchId ? { branch_id: parseInt(branchId, 10) } : {};
      const response = await api.get('/objects/next-code', { params });
      return response.data?.code || '';
    } catch (error) {
      console.error('Error fetching next code:', error);
      return '';
    }
  };

  const handleBranchChangeInModal = async (newBranchId) => {
    setFormData((prev) => ({ ...prev, branch_id: newBranchId, code: '...' }));
    const code = await fetchNextCode(newBranchId);
    setFormData((prev) => ({ ...prev, code }));
  };

  const openModal = async (obj = null) => {
    if (obj) {
      setEditingObject(obj);
      setFormData({
        name: obj.name,
        code: obj.code || '',
        branch_id: obj.branch_id != null ? String(obj.branch_id) : '',
        address: obj.address || '',
        description: obj.description || '',
        shift_start: obj.shift_start || '06:00',
        shift_end: obj.shift_end || '21:00',
        grace_period_mins: obj.grace_period_mins != null ? obj.grace_period_mins : 15,
        is_active: obj.is_active,
      });
      setShowModal(true);
    } else {
      setEditingObject(null);
      const defaultBranchId = user?.branch_id
        ? String(user.branch_id)
        : (branchFilter || (branches.length > 0 ? String(branches[0].id) : ''));
      setFormData({
        name: '',
        code: '...',
        branch_id: defaultBranchId,
        address: '',
        description: '',
        shift_start: '06:00',
        shift_end: '21:00',
        grace_period_mins: 15,
        is_active: true,
      });
      setShowModal(true);
      const suggestedCode = await fetchNextCode(defaultBranchId);
      setFormData((prev) => ({ ...prev, code: suggestedCode }));
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingObject(null);
    setFormData({
      name: '',
      code: '',
      branch_id: '',
      address: '',
      description: '',
      shift_start: '06:00',
      shift_end: '21:00',
      grace_period_mins: 15,
      is_active: true,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let cleanCode = formData.code ? formData.code.trim() : null;
      if (cleanCode === '...' || cleanCode === '') {
        cleanCode = null;
      }
      const payload = {
        name: formData.name.trim(),
        code: cleanCode,
        branch_id: formData.branch_id ? parseInt(formData.branch_id, 10) : null,
        address: formData.address.trim() || null,
        description: formData.description.trim() || null,
        shift_start: formData.shift_start || '06:00',
        shift_end: formData.shift_end || '21:00',
        grace_period_mins: parseInt(formData.grace_period_mins, 10) || 15,
        is_active: formData.is_active,
      };

      if (editingObject) {
        await api.patch(`/objects/${editingObject.id}`, payload);
      } else {
        await api.post('/objects', payload);
      }
      closeModal();
      fetchObjects();
    } catch (error) {
      alert(error.response?.data?.detail || 'Ошибка сохранения');
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/objects/${id}`);
      setDeleteConfirm(null);
      fetchObjects();
    } catch (error) {
      alert(error.response?.data?.detail || 'Ошибка удаления');
    }
  };

  const totalPages = Math.ceil(total / perPage);

  return (
    <div className="objects-page">
      <div className="page-header">
        <h2>Охранные объекты</h2>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          <div className="view-mode-toggle">
            <button
              className={`view-mode-btn ${viewMode === 'table' ? 'active' : ''}`}
              onClick={() => { setViewMode('table'); setPage(1); }}
            >
              📋 Таблица
            </button>
            <button
              className={`view-mode-btn ${viewMode === 'grouped' ? 'active' : ''}`}
              onClick={() => { setViewMode('grouped'); setPage(1); }}
            >
              🏢 По филиалам
            </button>
          </div>
          {hasRole(['admin', 'manager']) && (
            <button className="btn-primary" onClick={() => openModal()}>
              + Добавить объект
            </button>
          )}
        </div>
      </div>

      <div className="filters">
        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Поиск по коду, названию или адресу..."
          />
          {hasRole(['admin']) && (
            <select
              className="filter-select"
              value={branchFilter}
              onChange={handleBranchFilterChange}
            >
              <option value="">Все филиалы</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name} {b.code ? `(${b.code})` : ''}
                </option>
              ))}
            </select>
          )}
          <button type="submit" className="btn-secondary">
            Поиск
          </button>
        </form>
      </div>

      {loading ? (
        <div className="loading">Загрузка...</div>
      ) : viewMode === 'table' ? (
        /* Табличный вид */
        <>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Код</th>
                  <th>Название</th>
                  <th>Филиал</th>
                  <th>Адрес</th>
                  <th>График</th>
                  <th>Статус</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                {objects.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center">
                      Нет объектов
                    </td>
                  </tr>
                ) : (
                  objects.map((obj) => (
                    <tr key={obj.id}>
                      <td>{obj.id}</td>
                      <td>
                        {obj.code ? (
                          <span className="object-code-badge">{obj.code}</span>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td><strong>{obj.name}</strong></td>
                      <td>
                        {obj.branch_name ? (
                          <span className="branch-badge">{obj.branch_name}</span>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td>{obj.address || '-'}</td>
                      <td>
                        {obj.shift_start && obj.shift_end
                          ? `${obj.shift_start} – ${obj.shift_end}`
                          : '-'}
                      </td>
                      <td>
                        <span className={`status ${obj.is_active ? 'active' : 'inactive'}`}>
                          {obj.is_active ? 'Активен' : 'Неактивен'}
                        </span>
                      </td>
                      <td className="actions">
                        {hasRole(['admin', 'manager']) && (
                          <button
                            className="btn-icon btn-edit"
                            onClick={() => openModal(obj)}
                            title="Редактировать"
                          >
                            ✏️
                          </button>
                        )}
                        {hasRole(['admin']) && (
                          <button
                            className="btn-icon btn-delete"
                            onClick={() => setDeleteConfirm(obj)}
                            title="Удалить"
                          >
                            🗑️
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="btn-secondary"
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                Назад
              </button>
              <span className="page-info">
                Страница {page} из {totalPages} ({total} объектов)
              </span>
              <button
                className="btn-secondary"
                disabled={page >= totalPages}
                onClick={() => setPage(page + 1)}
              >
                Вперёд
              </button>
            </div>
          )}
        </>
      ) : (
        /* Вид с группировкой по филиалам */
        <div className="branch-groups-container">
          {groupedObjects.length === 0 ? (
            <div className="table-container text-center">Нет объектов для отображения</div>
          ) : (
            groupedObjects.map((group) => {
              const isCollapsed = collapsedBranches[group.name];
              return (
                <div key={group.name} className="branch-group-card">
                  <div
                    className="branch-group-header"
                    onClick={() => toggleBranchCollapse(group.name)}
                  >
                    <div className="branch-group-title">
                      <span>🏢</span>
                      <h3>{group.name}</h3>
                    </div>
                    <div className="branch-group-stats">
                      <span className="branch-stat-pill">
                        Постов: {group.total}
                      </span>
                      <span className="branch-stat-pill active-count">
                        Активных: {group.active}
                      </span>
                      <span style={{ fontSize: '0.85rem', color: '#64748b', marginLeft: '0.5rem' }}>
                        {isCollapsed ? '▼ Развернуть' : '▲ Свернуть'}
                      </span>
                    </div>
                  </div>

                  {!isCollapsed && (
                    <div className="branch-group-body">
                      <div className="table-container" style={{ margin: 0, border: 'none', borderRadius: 0 }}>
                        <table className="table">
                          <thead>
                            <tr>
                              <th>Код</th>
                              <th>Название поста</th>
                              <th>Адрес</th>
                              <th>График</th>
                              <th>Статус</th>
                              <th>Действия</th>
                            </tr>
                          </thead>
                          <tbody>
                            {group.items.map((obj) => (
                              <tr key={obj.id}>
                                <td>
                                  {obj.code ? (
                                    <span className="object-code-badge">{obj.code}</span>
                                  ) : (
                                    '-'
                                  )}
                                </td>
                                <td><strong>{obj.name}</strong></td>
                                <td>{obj.address || '-'}</td>
                                <td>
                                  {obj.shift_start && obj.shift_end
                                    ? `${obj.shift_start} – ${obj.shift_end}`
                                    : '-'}
                                </td>
                                <td>
                                  <span className={`status ${obj.is_active ? 'active' : 'inactive'}`}>
                                    {obj.is_active ? 'Активен' : 'Неактивен'}
                                  </span>
                                </td>
                                <td className="actions">
                                  {hasRole(['admin', 'manager']) && (
                                    <button
                                      className="btn-icon btn-edit"
                                      onClick={() => openModal(obj)}
                                      title="Редактировать"
                                    >
                                      ✏️
                                    </button>
                                  )}
                                  {hasRole(['admin']) && (
                                    <button
                                      className="btn-icon btn-delete"
                                      onClick={() => setDeleteConfirm(obj)}
                                      title="Удалить"
                                    >
                                      🗑️
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>{editingObject ? 'Редактировать объект' : 'Новый объект'}</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Название объекта *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Например, Пост №1 - Главные ворота"
                  required
                />
              </div>

              <div className="form-row">
                {hasRole(['admin']) && (
                  <div className="form-group">
                    <label>Привязка к филиалу</label>
                    <select
                      value={formData.branch_id}
                      onChange={(e) => handleBranchChangeInModal(e.target.value)}
                    >
                      <option value="">Без филиала</option>
                      {branches.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.name} {b.code ? `(${b.code})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="form-group">
                  <label>Код объекта (пост охраны)</label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    placeholder="Автоматически (например, 0143)"
                    maxLength={10}
                  />
                  <small style={{ color: '#666', fontSize: '0.8rem', display: 'block', marginTop: '4px' }}>
                    4-значный код (генерируется автоматически, можно изменить)
                  </small>
                </div>
              </div>

              <div className="form-group">
                <label>Адрес</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="г. Москва, ул. Ленина, д. 10"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Время начала смены</label>
                  <input
                    type="text"
                    value={formData.shift_start}
                    onChange={(e) => setFormData({ ...formData, shift_start: e.target.value })}
                    placeholder="06:00"
                    maxLength={5}
                  />
                </div>
                <div className="form-group">
                  <label>Время окончания смены</label>
                  <input
                    type="text"
                    value={formData.shift_end}
                    onChange={(e) => setFormData({ ...formData, shift_end: e.target.value })}
                    placeholder="21:00"
                    maxLength={5}
                  />
                </div>
                <div className="form-group">
                  <label>Допустимое опоздание (мин)</label>
                  <input
                    type="number"
                    value={formData.grace_period_mins}
                    onChange={(e) => setFormData({ ...formData, grace_period_mins: e.target.value })}
                    min={0}
                    max={120}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Описание</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                  placeholder="Дополнительные инструкции для диспетчера..."
                />
              </div>

              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                  {' '}Активен
                </label>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={closeModal}>
                  Отмена
                </button>
                <button type="submit" className="btn-primary">
                  {editingObject ? 'Сохранить' : 'Создать'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="modal modal-small" onClick={(e) => e.stopPropagation()}>
            <h3>Подтверждение удаления</h3>
            <p>Вы уверены, что хотите удалить объект <strong>{deleteConfirm.name}</strong>?</p>
            <div className="modal-actions">
              <button
                className="btn-secondary"
                onClick={() => setDeleteConfirm(null)}
              >
                Отмена
              </button>
              <button className="btn-danger" onClick={() => handleDelete(deleteConfirm.id)}>
                Удалить
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Objects;
